// 页：实验中心（A/B 闭环）
// 4 tab：实验列表 / 曝光与分桶 / 显著性面板 / 实验×候选
// spec §4.9 锚点：
//   - experimentKey / variantKey 是接口字段（前端传 key 不是 id），后端解析
//   - 客户端 hash(device_id, exp_key) 确定性分桶 → 同 device 7 天 100% 同桶（数字化验收 ④）
//   - 首曝快照 UNIQUE(experiment_id, device_id) 只记第一次
//   - GET /idp/experiments/config?webKey= 活跃实验配置，强缓存
//   - 分流由前端做，曝光事件回灌到 idp_event + exposure 表

import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Statistic, Tag, Select, Empty, Progress, Table, Alert, Tooltip } from 'antd';
import { Tabs } from '../AntdCompat';
import { ApiOutlined, ExperimentOutlined, CheckCircleTwoTone, WarningTwoTone, CloseCircleTwoTone, BulbOutlined } from '@ant-design/icons';
import {
  idpExperiments, idpVariants, idpExposures, idpSignificanceResults,
  experimentDailyRollups, SITE_NAMES,
  getExperimentCandidateCrosstab,
} from '../data';
import { DeviceType, SiteId } from '../types';

const STATUS_COLOR: Record<string, string> = {
  RUNNING: 'processing', PAUSED: 'warning', STOPPED: 'default', DRAFT: 'default',
};

const SpecCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="info"
    showIcon
    icon={<ApiOutlined />}
    message={<span style={{ fontSize: 12 }}>{children}</span>}
    style={{ marginBottom: 8 }}
  />
);
const BizCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="success"
    showIcon
    icon={<BulbOutlined />}
    message={<span style={{ fontSize: 12 }}><strong>业务方怎么用：</strong>{children}</span>}
    style={{ marginBottom: 12 }}
  />
);

const ExperimentCenter: React.FC = () => {
  const [selectedExpId, setSelectedExpId] = useState<string>(idpExperiments[0]?.experiment_id || '');

  const selectedExp = idpExperiments.find(e => e.experiment_id === selectedExpId);
  const expVariants = idpVariants.filter(v => v.experiment_id === selectedExpId);
  const expExposures = idpExposures.filter(e => e.experiment_id === selectedExpId);
  const expSignificance = idpSignificanceResults.find(s => s.experiment_id === selectedExpId);
  const expRollups = experimentDailyRollups.filter(r => r.experiment_id === selectedExpId);
  const expCandidateCross = useMemo(() => getExperimentCandidateCrosstab(selectedExpId), [selectedExpId]);

  // 整体统计
  const runningCount = idpExperiments.filter(e => e.status === 'RUNNING').length;
  const totalExposures = idpExposures.length;
  const significantCount = idpSignificanceResults.filter(r => r.is_significant).length;

  // 分桶质量：device_type 分布
  const bucketDeviceDist = useMemo(() => {
    const m: Record<string, Record<DeviceType, number>> = {};
    expVariants.forEach(v => {
      m[v.variant_id] = { mobile: 0, desktop: 0, tablet: 0 };
    });
    expExposures.forEach(e => {
      if (e.device_type_snapshot && m[e.variant_id]) {
        m[e.variant_id][e.device_type_snapshot]++;
      }
    });
    return m;
  }, [expVariants, expExposures]);

  const bucketSiteDist = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    expVariants.forEach(v => {
      m[v.variant_id] = { '0': 0, '1': 0, '2': 0 };
    });
    expExposures.forEach(e => {
      if (e.site_id_snapshot !== undefined && m[e.variant_id]) {
        m[e.variant_id][String(e.site_id_snapshot)]++;
      }
    });
    return m;
  }, [expVariants, expExposures]);

  // 时序：experiment_daily 转化率走势
  const dailySeries = useMemo(() => {
    const byDate: Record<string, Record<string, { exp: number; conv: number; rate: number }>> = {};
    expRollups.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = {};
      byDate[r.date][r.variant_id] = {
        exp: r.exposure_count, conv: r.conversion_count, rate: r.conversion_rate,
      };
    });
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  }, [expRollups]);

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* 顶部 spec 锚点 + 实验选择 */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={12}>
          <Col flex="100px"><strong>🧪 实验中心</strong></Col>
          <Col flex="auto">
            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
              spec §4.9 · 客户端 hash(device_id, exp_key) 分桶 · 首曝快照 UNIQUE(experiment_id, device_id) · GET /idp/experiments/config 强缓存
            </span>
          </Col>
          <Col>
            <span style={{ marginRight: 4 }}>实验:</span>
            <Select value={selectedExpId} onChange={setSelectedExpId} style={{ width: 240 }} size="small">
              {idpExperiments.map(e => (
                <Select.Option key={e.experiment_id} value={e.experiment_id}>{e.experiment_key} - {e.name}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Tabs
        items={[
          // ============================================================
          // Tab 1：实验列表
          // ============================================================
          {
            key: 'list',
            label: '① 实验列表',
            children: (
              <>
                <BizCallout>
                  实验"在跑没在跑"的总盘看板。绿灯（p&lt;0.05+正向 lift）= 可全量铺开；红灯（p&lt;0.05+负向）= 立即下线；黄灯（功效 &lt;80%）= 样本不够再观察。点击行查实验详情，跳曝光/显著性 tab 做决策。
                </BizCallout>
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={6}><Card size="small"><Statistic title="进行中" value={runningCount} prefix={<ExperimentOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="累计首曝" value={totalExposures} suffix="device" /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="显著实验" value={significantCount} suffix={`/ ${idpSignificanceResults.length}`} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col span={6}><Card size="small"><Statistic title="平均功效（Power）" value={(idpSignificanceResults.reduce((s, r) => s + r.power, 0) / idpSignificanceResults.length * 100).toFixed(0)} suffix="%" /></Card></Col>
                </Row>

                <Card size="small" title="活跃实验（来自 GET /idp/experiments/config 强缓存）">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="experiment_id"
                    onRow={(record) => ({ onClick: () => setSelectedExpId(record.experiment_id) })}
                    rowClassName={(r) => r.experiment_id === selectedExpId ? 'ant-table-row-selected' : ''}
                    dataSource={idpExperiments.map(e => {
                      const sig = idpSignificanceResults.find(s => s.experiment_id === e.experiment_id);
                      const expCount = idpExposures.filter(ex => ex.experiment_id === e.experiment_id).length;
                      return { ...e, sig, expCount };
                    })}
                    columns={[
                      { title: 'experiment_key', dataIndex: 'experiment_key', render: t => <code style={{ fontSize: 11 }}>{t}</code> },
                      { title: 'name', dataIndex: 'name' },
                      { title: '状态', dataIndex: 'status', render: s => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
                      { title: '已运行', dataIndex: 'start_at', render: (t: string) => {
                        const days = Math.floor((Date.now() - new Date(t).getTime()) / 86400000);
                        return `${days}d`;
                      } },
                      { title: '首曝 device', dataIndex: 'expCount' },
                      { title: '指标', render: (_, r: any) => r.sig?.primary_metric || '-' },
                      {
                        title: 'p-value',
                        render: (_, r: any) => r.sig ? (
                          <span style={{ color: r.sig.is_significant ? '#52c41a' : '#8c8c8c' }}>
                            <strong>{r.sig.p_value.toFixed(3)}</strong>
                            {r.sig.is_significant ? <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginLeft: 4 }} /> : null}
                          </span>
                        ) : '-',
                      },
                      {
                        title: 'MDE',
                        render: (_, r: any) => r.sig ? (
                          <Tooltip title={`目标 ${r.sig.min_detectable_effect_pct}% lift；当前样本能否检测到`}>
                            {r.sig.has_reached_mde ? <Tag color="green">达 MDE</Tag> : <Tag color="orange">未达</Tag>}
                          </Tooltip>
                        ) : '-',
                      },
                    ]}
                  />
                </Card>

                {selectedExp && (
                  <Card size="small" title={`详情：${selectedExp.name}`} style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#595959', marginBottom: 8 }}>{selectedExp.description}</div>
                    <Row gutter={12}>
                      {expVariants.map(v => {
                        const expCount = expExposures.filter(e => e.variant_id === v.variant_id).length;
                        return (
                          <Col span={12} key={v.variant_id}>
                            <Card size="small" type="inner">
                              <Tag color={v.variant_key === 'control' ? 'default' : 'cyan'}>{v.variant_key}</Tag>
                              <strong style={{ marginLeft: 8 }}>{v.name}</strong>
                              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                allocation: <strong>{(v.allocation * 100).toFixed(0)}%</strong> · 首曝 <strong>{expCount}</strong> device
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card>
                )}
              </>
            ),
          },

          // ============================================================
          // Tab 2：曝光与分桶
          // ============================================================
          {
            key: 'exposure',
            label: '② 曝光与分桶',
            children: (
              <>
                <SpecCallout>
                  <strong>首曝快照表 UNIQUE(experiment_id, device_id)</strong> — 每个 device 在每个实验中只记第一次曝光。
                  分桶由<strong>客户端 hash(device_id, exp_key)</strong> 确定性产生，同 device 7 天内 100% 同桶（数字化验收 ④）
                </SpecCallout>
                <BizCallout>
                  这页确认<strong>实验有没有公平地在跑</strong>。如果 control 全 mobile / treatment 全 desktop，看似的转化差异其实是设备差异 — <strong>这种情况显著性面板的结论就是假的</strong>。χ² 卡方平衡 = 可信；失衡 = 重抽样或换分流算法。这是看显著性面板之前必须先看的"前置检查页"。
                </BizCallout>

                {!selectedExp ? <Empty /> : (
                  <>
                    <Card size="small" title={`首曝快照表（${selectedExp.experiment_key}）`} style={{ marginBottom: 12 }}>
                      <Table
                        size="small"
                        pagination={{ pageSize: 8 }}
                        rowKey="id"
                        dataSource={expExposures.slice(0, 50)}
                        columns={[
                          { title: 'id', dataIndex: 'id', render: t => <code style={{ fontSize: 10 }}>{t}</code> },
                          { title: 'device_id', dataIndex: 'device_id', render: t => <code style={{ fontSize: 10 }}>{t}</code> },
                          {
                            title: 'variant',
                            dataIndex: 'variant_id',
                            render: vid => {
                              const v = idpVariants.find(x => x.variant_id === vid);
                              return <Tag color={v?.variant_key === 'control' ? 'default' : 'cyan'}>{v?.variant_key}</Tag>;
                            },
                          },
                          { title: 'first_exposure_at', dataIndex: 'first_exposure_at', render: t => <small>{new Date(t).toLocaleString('zh-CN')}</small> },
                          {
                            title: 'device_snapshot', dataIndex: 'device_type_snapshot',
                            render: dt => dt ? <Tag>{dt}</Tag> : '-',
                          },
                          {
                            title: 'site_snapshot', dataIndex: 'site_id_snapshot',
                            render: (s: SiteId) => s !== undefined ? <Tag>{SITE_NAMES[s]}</Tag> : '-',
                          },
                        ]}
                      />
                    </Card>

                    <Row gutter={12}>
                      <Col span={12}>
                        <Card size="small" title="分桶质量 — device_type 分布平衡性">
                          {expVariants.map(v => {
                            const dist = bucketDeviceDist[v.variant_id] || { mobile: 0, desktop: 0, tablet: 0 };
                            const total = dist.mobile + dist.desktop + dist.tablet;
                            return (
                              <div key={v.variant_id} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                  <span><Tag>{v.variant_key}</Tag>{v.name}</span>
                                  <span>n={total}</span>
                                </div>
                                <div style={{ display: 'flex', height: 18, borderRadius: 3, overflow: 'hidden' }}>
                                  <Tooltip title={`mobile ${dist.mobile} (${total > 0 ? (dist.mobile / total * 100).toFixed(0) : 0}%)`}>
                                    <div style={{ width: `${total > 0 ? dist.mobile / total * 100 : 0}%`, background: '#1890ff', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: '18px' }}>m</div>
                                  </Tooltip>
                                  <Tooltip title={`desktop ${dist.desktop} (${total > 0 ? (dist.desktop / total * 100).toFixed(0) : 0}%)`}>
                                    <div style={{ width: `${total > 0 ? dist.desktop / total * 100 : 0}%`, background: '#52c41a', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: '18px' }}>d</div>
                                  </Tooltip>
                                  <Tooltip title={`tablet ${dist.tablet} (${total > 0 ? (dist.tablet / total * 100).toFixed(0) : 0}%)`}>
                                    <div style={{ width: `${total > 0 ? dist.tablet / total * 100 : 0}%`, background: '#fa8c16', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: '18px' }}>t</div>
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          })}
                          {expSignificance && (
                            <Alert
                              type={expSignificance.bucket_quality.is_balanced ? 'success' : 'warning'}
                              showIcon
                              message={
                                <span style={{ fontSize: 11 }}>
                                  device_type χ² = <strong>{expSignificance.bucket_quality.device_type_chi2.toFixed(2)}</strong>
                                  {expSignificance.bucket_quality.is_balanced ? '（平衡，可继续分析）' : '（失衡，需重抽样）'}
                                </span>
                              }
                            />
                          )}
                        </Card>
                      </Col>

                      <Col span={12}>
                        <Card size="small" title="分桶质量 — site 分布平衡性">
                          {expVariants.map(v => {
                            const dist = bucketSiteDist[v.variant_id] || {};
                            const total = (dist['0'] || 0) + (dist['1'] || 0) + (dist['2'] || 0);
                            return (
                              <div key={v.variant_id} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                  <span><Tag>{v.variant_key}</Tag>{v.name}</span>
                                  <span>n={total}</span>
                                </div>
                                <div style={{ display: 'flex', height: 18, borderRadius: 3, overflow: 'hidden' }}>
                                  {([0, 1, 2] as SiteId[]).map(s => {
                                    const colors = ['#1890ff', '#722ed1', '#fa541c'];
                                    return (
                                      <Tooltip key={s} title={`${SITE_NAMES[s]} ${dist[String(s)] || 0}`}>
                                        <div style={{
                                          width: `${total > 0 ? (dist[String(s)] || 0) / total * 100 : 0}%`,
                                          background: colors[s], textAlign: 'center', color: '#fff',
                                          fontSize: 10, lineHeight: '18px',
                                        }}>{SITE_NAMES[s]}</div>
                                      </Tooltip>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          {expSignificance && (
                            <Alert
                              type={expSignificance.bucket_quality.is_balanced ? 'success' : 'warning'}
                              showIcon
                              message={
                                <span style={{ fontSize: 11 }}>
                                  site χ² = <strong>{expSignificance.bucket_quality.site_chi2.toFixed(2)}</strong>
                                  {expSignificance.bucket_quality.is_balanced ? '（平衡）' : '（失衡）'}
                                </span>
                              }
                            />
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            ),
          },

          // ============================================================
          // Tab 3：显著性面板
          // ============================================================
          {
            key: 'significance',
            label: '③ 显著性面板',
            children: (
              <>
                <SpecCallout>
                  统计口径：two-proportion z-test，α=0.05（p ≤0.05 即显著）；置信区间 95%；MDE = 当前样本量能可靠检测的最小相对 lift；power（功效）目标 ≥0.80
                </SpecCallout>
                <BizCallout>
                  这是<strong>做决策的页面</strong>。看四件事：① p&lt;0.05? 显著 → 可以行动 ② lift 正向还是负向 → 全量铺还是放弃 ③ 达 MDE 没？ → 没达就继续观察 ④ Power ≥80%? → 不到说明样本太少，结论可能假。结论 Alert 直接告诉你"该全量铺 / 该放弃 / 该再等"。
                </BizCallout>

                {!expSignificance ? <Empty /> : (
                  <>
                    {/* 顶部 6 项 KPI */}
                    <Row gutter={12} style={{ marginBottom: 12 }}>
                      <Col span={4}><Card size="small">
                        <Statistic title="primary metric" value={expSignificance.primary_metric} valueStyle={{ fontSize: 16 }} />
                      </Card></Col>
                      <Col span={4}><Card size="small">
                        <Statistic
                          title="p-value"
                          value={expSignificance.p_value.toFixed(4)}
                          valueStyle={{ color: expSignificance.is_significant ? '#52c41a' : '#cf1322', fontSize: 18 }}
                        />
                      </Card></Col>
                      <Col span={4}><Card size="small">
                        <Statistic
                          title="Lift (relative)"
                          value={expSignificance.lift_relative_pct.toFixed(1)}
                          suffix="%"
                          valueStyle={{ color: expSignificance.lift_relative_pct > 0 ? '#52c41a' : '#cf1322' }}
                        />
                      </Card></Col>
                      <Col span={4}><Card size="small">
                        <Statistic title="95% CI" value={`[${expSignificance.confidence_interval_95[0]}%, ${expSignificance.confidence_interval_95[1]}%]`} valueStyle={{ fontSize: 14 }} />
                      </Card></Col>
                      <Col span={4}><Card size="small">
                        <Statistic
                          title="MDE 达成"
                          value={expSignificance.has_reached_mde ? 'YES' : 'NO'}
                          valueStyle={{ color: expSignificance.has_reached_mde ? '#52c41a' : '#fa8c16' }}
                          prefix={expSignificance.has_reached_mde ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <WarningTwoTone twoToneColor="#fa8c16" />}
                        />
                      </Card></Col>
                      <Col span={4}><Card size="small">
                        <Statistic
                          title="Power"
                          value={(expSignificance.power * 100).toFixed(0)}
                          suffix="%"
                          valueStyle={{ color: expSignificance.power >= 0.8 ? '#52c41a' : '#fa8c16' }}
                        />
                      </Card></Col>
                    </Row>

                    {/* control vs treatment */}
                    <Row gutter={12}>
                      <Col span={12}>
                        <Card size="small" title="Control（对照）" style={{ borderTop: '3px solid #8c8c8c' }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>variant_id: {expSignificance.control_variant_id}</div>
                          <Row gutter={8}>
                            <Col span={8}><Statistic title="样本量" value={expSignificance.control_n} /></Col>
                            <Col span={8}><Statistic title="转化数" value={expSignificance.control_conversions} /></Col>
                            <Col span={8}><Statistic title="转化率" value={(expSignificance.control_rate * 100).toFixed(2)} suffix="%" /></Col>
                          </Row>
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card size="small" title="Treatment（实验）" style={{
                          borderTop: `3px solid ${expSignificance.is_significant ? '#52c41a' : '#fa8c16'}`,
                        }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>variant_id: {expSignificance.treatment_variant_id}</div>
                          <Row gutter={8}>
                            <Col span={8}><Statistic title="样本量" value={expSignificance.treatment_n} /></Col>
                            <Col span={8}><Statistic title="转化数" value={expSignificance.treatment_conversions} valueStyle={{ color: '#52c41a' }} /></Col>
                            <Col span={8}><Statistic title="转化率" value={(expSignificance.treatment_rate * 100).toFixed(2)} suffix="%" valueStyle={{ color: '#52c41a' }} /></Col>
                          </Row>
                        </Card>
                      </Col>
                    </Row>

                    {/* 结论 */}
                    <Alert
                      type={expSignificance.is_significant ? (expSignificance.lift_relative_pct > 0 ? 'success' : 'error') : 'info'}
                      showIcon
                      style={{ marginTop: 12 }}
                      message={
                        <span>
                          <strong>结论：</strong>
                          {expSignificance.is_significant
                            ? (expSignificance.lift_relative_pct > 0
                              ? `Treatment 显著优于 Control（p=${expSignificance.p_value.toFixed(3)} < 0.05，lift +${expSignificance.lift_relative_pct.toFixed(1)}%），建议全量铺开`
                              : `Treatment 显著劣于 Control（p=${expSignificance.p_value.toFixed(3)} < 0.05，lift ${expSignificance.lift_relative_pct.toFixed(1)}%），建议放弃实验`)
                            : `差异未达统计显著（p=${expSignificance.p_value.toFixed(3)} > 0.05）。当前 power=${(expSignificance.power * 100).toFixed(0)}%${expSignificance.has_reached_mde ? '，但样本已达 MDE，继续观察价值有限' : '，建议增加样本量直到达 MDE'}`
                          }
                        </span>
                      }
                    />

                    {/* 时序：每日转化率走势 */}
                    {dailySeries.length > 0 && (
                      <Card size="small" title="每日转化率（control vs treatment）" style={{ marginTop: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <th style={{ padding: 4, textAlign: 'left' }}>日期</th>
                              {expVariants.map(v => (
                                <th key={v.variant_id} style={{ padding: 4 }}>
                                  <Tag color={v.variant_key === 'control' ? 'default' : 'cyan'}>{v.variant_key}</Tag>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dailySeries.map(([date, m]) => (
                              <tr key={date} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                                <td style={{ padding: 4 }}>{date}</td>
                                {expVariants.map(v => {
                                  const d = m[v.variant_id];
                                  return (
                                    <td key={v.variant_id} style={{ padding: 4, textAlign: 'center' }}>
                                      {d ? (
                                        <span>
                                          <strong>{(d.rate * 100).toFixed(2)}%</strong>
                                          <small style={{ color: '#8c8c8c', marginLeft: 4 }}>({d.conv}/{d.exp})</small>
                                        </span>
                                      ) : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Card>
                    )}
                  </>
                )}
              </>
            ),
          },

          // ============================================================
          // Tab 4：实验×候选
          // ============================================================
          {
            key: 'crosstab',
            label: '④ 实验 × 候选',
            children: (
              <>
                <SpecCallout>
                  交叉分析：同 AI 候选款在不同 variant 下的曝光/转化差异 — 揭示"AI 模板调整对哪些候选有 lift"
                </SpecCallout>
                <BizCallout>
                  揭示<strong>"实验对所有候选都有用吗？"</strong> 同一个改动（比如长描述）可能哥特款有 lift、极简款没有。这页让你做<strong>分品类应用</strong>决策——不是"全量铺"或"全放弃"二选一，而是"哪些候选用 winning variant、哪些保持 control"。
                </BizCallout>

                {expCandidateCross.length === 0 ? (
                  <Empty description="该实验暂无候选维度数据（实验未关联具体 candidate_id）" />
                ) : (
                  <Card size="small" title={`${selectedExp?.experiment_key} × candidate_id`}>
                    <Table
                      size="small"
                      rowKey={r => `${r.candidate_id}_${r.variant_id}`}
                      dataSource={expCandidateCross}
                      pagination={{ pageSize: 15 }}
                      columns={[
                        { title: 'candidate_id', dataIndex: 'candidate_id', render: t => <code style={{ fontSize: 10 }}>{t}</code> },
                        { title: '候选标题', dataIndex: 'title', render: t => t || <span style={{ color: '#bfbfbf' }}>(未关联)</span> },
                        {
                          title: 'variant',
                          render: (_, r: any) => <Tag color={r.variant_key === 'control' ? 'default' : 'cyan'}>{r.variant_key}</Tag>,
                        },
                        { title: '曝光（PV）', dataIndex: 'exposures' },
                        { title: '转化（加购/购）', dataIndex: 'conversions', render: (n: number) => <span style={{ color: '#52c41a' }}>{n}</span> },
                        {
                          title: '转化率',
                          dataIndex: 'rate',
                          render: (r: number) => (
                            <span>
                              <Progress percent={r * 100} size="small" style={{ width: 80, marginRight: 6 }} />
                              <strong>{(r * 100).toFixed(2)}%</strong>
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ExperimentCenter;
